'use client';

import { useSyncExternalStore } from 'react';
import { domainError, errorMessage, type Command, type DomainError } from '@/domain/types';
import { m, type Message } from '@/i18n';
import type { QueuedGesture, ReplayOutcome } from '@/server/replay';
import { surfaceCursor } from './live-store';
import { loadQueue, persistQueue, type StoredCommand, type StoredGesture } from './live-idb';
import { isNetworkFailure, isOffline, withinReplyLimit } from '@/lib/reach';

/**
 * The offline command queue (docs/SYNC.md §7.4, docs/MOBILE.md §8): a tap that
 * cannot reach the server is not refused, it is DEFERRED. The device keeps the
 * intended COMMANDS - never events, never rows - each with an idempotency key,
 * the device time of the tap, and the cursor it was taken against; on reconnect
 * they replay in device order through one gated endpoint, and the server decides
 * each one through `decide` exactly as it decides a live command.
 *
 * What the device is trusted for is the ORDER AMONG LEGAL ACTS, nothing else:
 * identity is the session at replay (the queue carries no actor), legality is
 * re-decided at arrival, and `occurredAt` is clamped server-side into the window
 * between the cursor's issue and the arrival. A command that became illegal
 * while the phone was in the shop comes back refused with its typed error, which
 * the row speaks - the existing notice contract, never a silent drop.
 *
 * The optimistic half is an OVERLAY, never the record (docs/SYNC.md §7.3): the
 * queue holds, per command, what the row is EXPECTED to become, and the surface
 * renders that expectation as a pending reading (the checklist line's
 * `data-ticked`). The record's own atoms stay server truth and change only when
 * the server's row arrives - a push, an accepted replay with its `at`, or a
 * refresh. A refused act drops its expectation and speaks its typed error on
 * the row; there is nothing to revert, because nothing was written.
 */

/** One command's outcome, as `replayCommands` (src/server/replay.ts) answers
 *  it: the server's own type, so the wire cannot drift. */
export type ReplayResult = ReplayOutcome;

/** The replay batch limit the endpoint accepts (SYNC.md §7.4: bounded
 *  batches under a rate limit). */
export const REPLAY_BATCH = 100;

/** The queue's limit (SYNC.md §7.4: "a few hundred commands, with a notice").
 *  A device that keeps acting past it is told, and the tap is refused rather
 *  than silently lost. */
export const QUEUE_LIMIT = 500;

// --- the pure half --------------------------------------------------------

/**
 * A fresh idempotency key: the device counter, a timestamp, and a random tail.
 * NOT `crypto.randomUUID` - the dev tier is reached from phones over plain http
 * on the LAN, where the secure-context crypto API is undefined
 * (.claude/rules/ui-kit.md). Uniqueness only has to hold per device and person;
 * the server's receipt (`events.command_key`, unique per principal and key) is
 * what makes a replay idempotent. Pure given its inputs.
 */
export function mintKey(counter: number, now: number, random: number): string {
  return `${now.toString(36)}-${counter.toString(36)}-${Math.floor(random * 0xffffff).toString(36)}`;
}

/** Append to the queue under the bound. Returns the new queue and whether the
 *  command was refused because the queue is full. Pure. */
export function enqueueEntry(
  queue: readonly StoredCommand[],
  entry: StoredCommand,
  limit = QUEUE_LIMIT,
): { queue: StoredCommand[]; full: boolean } {
  if (queue.length >= limit) return { queue: [...queue], full: true };
  return { queue: [...queue, entry], full: false };
}

/** The queue split into replay batches, device order preserved. Pure. */
export function batches(queue: readonly StoredCommand[], size = REPLAY_BATCH): StoredCommand[][] {
  const out: StoredCommand[][] = [];
  for (let i = 0; i < queue.length; i += size) out.push(queue.slice(i, i + size));
  return out;
}

/**
 * Fold the endpoint's answers into the queue, THREE ways (docs/RESOLUTION.md
 * §9, the replay through the dry run): an ACCEPTED key leaves (the write
 * landed; the store adopts the row when the push or the refresh carries
 * `updatedAt >= at`); a SUPERSEDED key leaves quietly (the truth already moved
 * past the tap - someone else ticked it - so there is nothing to say, only the
 * server's row to adopt); a REFUSED key leaves and reports its typed error for
 * the row to speak. A key the batch did not answer stays for the next attempt.
 * Pure.
 */
export function reconcileResults(
  queue: readonly StoredCommand[],
  results: readonly ReplayResult[],
): {
  queue: StoredCommand[];
  settled: string[];
  refused: { key: string; workItemId: string; error: DomainError }[];
  accepted: { key: string; workItemId: string; at: number }[];
  superseded: { key: string; workItemId: string; at: number }[];
} {
  const byKey = new Map(results.map((r) => [r.key, r]));
  const kept: StoredCommand[] = [];
  const settled: string[] = [];
  const refused: { key: string; workItemId: string; error: DomainError }[] = [];
  const accepted: { key: string; workItemId: string; at: number }[] = [];
  const superseded: { key: string; workItemId: string; at: number }[] = [];
  for (const entry of queue) {
    const result = byKey.get(entry.key);
    if (!result) {
      kept.push(entry);
      continue;
    }
    settled.push(entry.key);
    if (!result.ok) {
      refused.push({ key: entry.key, workItemId: entry.workItemId, error: result.error });
    } else if (result.superseded) {
      superseded.push({ key: entry.key, workItemId: entry.workItemId, at: result.at });
    } else {
      accepted.push({ key: entry.key, workItemId: entry.workItemId, at: result.at });
    }
  }
  return { queue: kept, settled, refused, accepted, superseded };
}

/**
 * What a command is EXPECTED to make of a row's atoms (docs/MOBILE.md §8:
 * "optimistic means the UI applies the expected result at the tap"). A fold
 * over the commands themselves - no record needed and none touched: the
 * expectation is an OVERLAY the surface renders as a pending reading, while
 * the row's own atoms stay the server's answer (docs/SYNC.md §7.3).
 *
 * Only the atoms a single-tap decision moves are read here; a command with no
 * atom reading (a title, a priority) folds to nothing and simply queues. Pure.
 */
export interface PendingAtoms {
  delivered?: boolean;
  closed?: boolean;
  active?: boolean;
}

export function expectedAtoms(commands: readonly Command[], base: PendingAtoms = {}): PendingAtoms {
  const out: PendingAtoms = { ...base };
  for (const command of commands) {
    switch (command.type) {
      case 'Tender':
        out.delivered = true;
        break;
      case 'Retract':
        out.delivered = false;
        break;
      case 'Close':
        out.closed = true;
        break;
      case 'Reopen':
        out.closed = false;
        break;
      case 'SetActive':
        out.active = command.active;
        break;
      default:
        break;
    }
  }
  return out;
}

/**
 * The pending overlay per ITEM, folded in device order, for the rows the
 * surfaces render. A deferred COMMAND folds through `expectedAtoms`; a deferred
 * GESTURE carries the expectation the device's own dry run produced (the plan
 * it resolved at the tap), because what a stimulus means is not readable from
 * the stimulus alone. A gesture whose plan SWEPT carries that expectation for
 * every other item it covered too (docs/RESOLUTION.md §6), so a deferred tick
 * on a category reads struck over its whole set and not only its own line.
 * Pure.
 */
export function pendingByItem(queue: readonly StoredCommand[]): ReadonlyMap<string, PendingAtoms> {
  const out = new Map<string, PendingAtoms>();
  for (const entry of queue) {
    const base = out.get(entry.workItemId) ?? {};
    const folded = entry.command
      ? expectedAtoms([entry.command], base)
      : { ...base, ...(entry.gesture?.expected ?? {}) };
    if (Object.keys(folded).length > 0) out.set(entry.workItemId, folded);
    for (const [itemId, atoms] of Object.entries(entry.gesture?.swept ?? {})) {
      out.set(itemId, { ...(out.get(itemId) ?? {}), ...atoms });
    }
  }
  return out;
}

/** Whether a failure is the network refusing (`src/lib/reach.ts`), and whether
 *  the device believes it is offline: one definition for the queue and for the
 *  acts it cannot carry. */
export { isNetworkFailure, isOffline };

// --- the runtime half -----------------------------------------------------

export interface QueueState {
  /** How many commands are waiting, for the shell's quiet indicator. */
  waiting: number;
  /** The queue refused a tap because it is full (SYNC.md §7.4's bound). */
  full: boolean;
  /** Refusals from the last replay, by work item: the row speaks them. */
  notices: ReadonlyMap<string, Message>;
  /** The PENDING OVERLAY (docs/SYNC.md §7.3): per work item, what the queued
   *  acts expect its atoms to become. The surfaces render it as a pending
   *  reading; the record itself stays the server's answer. */
  pending: ReadonlyMap<string, PendingAtoms>;
  version: number;
}

const EMPTY_NOTICES: ReadonlyMap<string, Message> = new Map();
const EMPTY_PENDING: ReadonlyMap<string, PendingAtoms> = new Map();

const queue: {
  entries: StoredCommand[];
  state: QueueState;
  listeners: Set<() => void>;
  counter: number;
  replaying: boolean;
  loaded: boolean;
} = {
  entries: [],
  state: { waiting: 0, full: false, notices: EMPTY_NOTICES, pending: EMPTY_PENDING, version: 0 },
  listeners: new Set(),
  counter: 0,
  replaying: false,
  loaded: false,
};

/** What the queue tells the app after any change. The pending overlay is
 *  DERIVED from the entries, so it can never disagree with them. */
function publish(patch: Partial<Omit<QueueState, 'version' | 'waiting' | 'pending'>> = {}): void {
  const pending = pendingByItem(queue.entries);
  queue.state = {
    ...queue.state,
    ...patch,
    waiting: queue.entries.length,
    pending: pending.size === 0 ? EMPTY_PENDING : pending,
    version: queue.state.version + 1,
  };
  for (const listener of queue.listeners) listener();
}

/**
 * What to run when a replay pass settled something: the refresh that brings the
 * server's rows for the acts that just landed (docs/SYNC.md §7.3 - the record
 * changes when the server's row arrives, never when the device decides). The
 * shell's `LiveDevice` registers the router's refresh; nothing else may.
 */
let onReplaySettled: (() => void) | null = null;

export function setReplayListener(listener: (() => void) | null): void {
  onReplaySettled = listener;
}

function subscribeQueue(listener: () => void): () => void {
  queue.listeners.add(listener);
  return () => {
    queue.listeners.delete(listener);
  };
}

function queueSnapshot(): QueueState {
  return queue.state;
}

/** The queue's state for the shell indicator, the row notices and the pending
 *  overlay the surfaces render. */
export function useQueueState(): QueueState {
  return useSyncExternalStore(subscribeQueue, queueSnapshot, queueSnapshot);
}

/** The pending overlay alone (docs/SYNC.md §7.3), for a surface that renders
 *  the deferred acts' expected reading beside its own in-flight one. */
export function usePendingAtoms(): ReadonlyMap<string, PendingAtoms> {
  return useSyncExternalStore(subscribeQueue, queueSnapshot, queueSnapshot).pending;
}

/** What the queue holds right now (exported for the tests and the indicator). */
export function queuedCommands(): readonly StoredCommand[] {
  return queue.entries;
}

/** The queue's state outside React (exported for the tests). */
export function queueState(): QueueState {
  return queue.state;
}

/** Read the persisted queue back after a reload. Called once, by the device
 *  component, after the identity has been adopted (so another person's queue is
 *  already gone). */
export async function loadPersistedQueue(): Promise<void> {
  if (queue.loaded) return;
  queue.loaded = true;
  const stored = await loadQueue();
  if (stored.length === 0) return;
  queue.entries = [...stored, ...queue.entries].sort((a, b) => a.seq - b.seq);
  queue.counter = Math.max(queue.counter, ...stored.map((s) => s.seq)) + 1;
  publish();
}

/** Drop everything the queue holds (a different person, or a new build).
 *  Exported for the tests. */
export function clearQueue(): void {
  const keys = queue.entries.map((e) => e.key);
  queue.entries = [];
  void persistQueue([], keys);
  publish({ full: false, notices: EMPTY_NOTICES });
}

/**
 * Defer one command: mint its key, stamp the device time and the cursor of the
 * surface it was taken on, and persist it. The row then reads its expected
 * state through the PENDING OVERLAY (`publish` derives it from the queue) -
 * the device's record is not touched, because what the record says about the
 * three atoms is the server's answer and nothing else (docs/SYNC.md §7.3).
 * Returns the key, or null when the queue is full (the caller speaks the
 * notice).
 */
export function enqueue(input: {
  workItemId: string;
  command?: Command;
  /** A resolver gesture instead of a command (docs/RESOLUTION.md §1 step 1). */
  gesture?: StoredGesture;
  /** The key the gesture already carries (a stimulus IS its key), so the
   *  deferred act and the one that failed to reach the server are one act.
   *  Absent for a command, which is keyed here. */
  key?: string;
  /** The surface the act was taken on, for the cursor the command is based on. */
  surfaceKey?: string;
}): string | null {
  const now = Date.now();
  const key = input.key ?? mintKey(queue.counter, now, Math.random());
  const entry: StoredCommand = {
    key,
    workItemId: input.workItemId,
    ...(input.command ? { command: input.command } : {}),
    ...(input.gesture ? { gesture: input.gesture } : {}),
    occurredAt: now,
    basedOn: input.surfaceKey ? surfaceCursor(input.surfaceKey) : null,
    seq: queue.counter,
  };
  const { queue: next, full } = enqueueEntry(queue.entries, entry);
  if (full) {
    publish({ full: true });
    return null;
  }
  queue.counter += 1;
  queue.entries = next;
  void persistQueue([entry], []);
  publish({ full: false });
  return key;
}

/**
 * Run an act, or queue it when the network is gone (docs/MOBILE.md §8, the
 * "a tap without network fails" gap): online the ordinary server-action path
 * stands, and only a NETWORK failure (or a device that already knows it is
 * offline) defers. A domain refusal is still a refusal and comes straight back.
 *
 * **An act with no reply after `REPLY_LIMIT_MS` is deferred too**
 * (docs/LOADING.md §5.6): a GESTURE moves into the queue under the key it
 * already carries, so the reading stays (the pending overlay) and the top bar
 * counts it, and the replay finds the first attempt's receipt on
 * `events.origin_key` if that attempt landed after all - nothing applies twice.
 * The first attempt is not cancelled (a server action cannot be); its late
 * reply settles the entry as the replay's answer would (`settleLate`). A raw
 * COMMAND is not moved at the limit: its first attempt carries no key (the
 * queue mints one at `enqueue`), so a replay could not know it had landed.
 */
export async function runOrQueue<T extends { ok: boolean }>(
  input: {
    workItemId: string;
    /** The commands to defer, for a surface that dispatches commands. */
    commands?: Command[];
    /** The gesture to defer, with the key it carries, for a surface on the
     *  resolver (docs/RESOLUTION.md §12 step 3). */
    gesture?: { key: string; gesture: StoredGesture };
    surfaceKey?: string;
  },
  run: () => Promise<T>,
  opts: { limitMs?: number } = {},
): Promise<T | { ok: true; queued: true; late?: true } | { ok: false; error: DomainError }> {
  const full = { ok: false as const, error: domainError('rate_limited', m('shell.queue.full')) };
  const defer = () => {
    if (input.gesture) {
      const key = enqueue({
        workItemId: input.workItemId,
        key: input.gesture.key,
        gesture: input.gesture.gesture,
        ...(input.surfaceKey ? { surfaceKey: input.surfaceKey } : {}),
      });
      return key === null ? full : { ok: true as const, queued: true as const };
    }
    for (const command of input.commands ?? []) {
      const key = enqueue({
        workItemId: input.workItemId,
        command,
        ...(input.surfaceKey ? { surfaceKey: input.surfaceKey } : {}),
      });
      if (key === null) return full;
    }
    return { ok: true as const, queued: true as const };
  };
  if (isOffline()) return defer();
  try {
    const attempt = run();
    if (!input.gesture) return await attempt;
    const answer = await withinReplyLimit(attempt, opts.limitMs);
    if (!answer.late) return answer.reply;
    const deferred = defer();
    if (!deferred.ok) {
      attempt.catch(() => {}); // refused by the full queue: the reply decides nothing here
      return deferred;
    }
    settleLate(input.gesture.key, input.workItemId, attempt);
    return { ...deferred, late: true as const };
  } catch (error) {
    if (!isNetworkFailure(error)) throw error;
    return defer();
  }
}

/**
 * The late reply of an act the limit moved into the queue (docs/LOADING.md
 * §5.6), settled the way `replayQueue` settles an entry when the replay
 * answers, whichever arrives first:
 *
 * - **the entry is gone** (the replay answered first, or the queue was
 *   cleared): the reply is dropped; the replay's answer already said it;
 * - **a confirmation**: the entry leaves, as an ACCEPTED key does, and the
 *   refresh brings the server's rows. The overlay and the reply cannot
 *   disagree here: the overlay is the device's own expectation of that same
 *   act, and it goes the moment the refresh can carry the truth;
 * - **a refusal**: the entry leaves, as a REFUSED key does, and its message
 *   joins the queue's `notices`, spoken where the act was taken (the item
 *   page's state line reads them; the checklist line does not yet, the board
 *   item "A tick refused at replay says nothing on the checklist line");
 * - **a late network failure** is no reply: the entry stays for the replay.
 *
 * The replay itself cannot overtake a first attempt from the same page: it is
 * a server action too, and the App Router runs server actions one at a time
 * (`dispatchAction`, next/dist/client/components/app-router-instance.js), so
 * the late reply lands first and the replay then finds nothing to send.
 */
function settleLate(key: string, workItemId: string, attempt: Promise<{ ok: boolean }>): void {
  attempt.then(
    (reply) => {
      if (!queue.entries.some((e) => e.key === key)) return;
      queue.entries = queue.entries.filter((e) => e.key !== key);
      void persistQueue([], [key]);
      if (!reply.ok) {
        const notices = new Map(queue.state.notices);
        notices.set(workItemId, refusalMessage(reply));
        publish({ notices, full: false });
        return;
      }
      publish({ full: false });
      onReplaySettled?.();
    },
    (error: unknown) => {
      // A late network failure is no reply and the replay carries the entry;
      // anything else is a bug and says so.
      if (!isNetworkFailure(error)) console.error(error);
    },
  );
}

/** A refusal's message, from the `{ ok: false, error }` shape every server
 *  action answers with. */
function refusalMessage(reply: { ok: boolean }): Message {
  const error = (reply as { error?: { message?: unknown; descriptor?: Message } }).error;
  return typeof error?.message === 'string'
    ? errorMessage({ message: error.message, descriptor: error.descriptor })
    : m('shell.queue.refused');
}

/**
 * Replay the queue in device order, in batches of at most `REPLAY_BATCH`
 * (SYNC.md §7.4). A settled key leaves the queue, and with it its pending
 * overlay: an ACCEPTED one because the server now holds the act (the refresh
 * this pass asks for brings its row), a SUPERSEDED one because the truth
 * already reads otherwise and the refresh brings that (no notice: nothing went
 * wrong), a REFUSED one because it never happened - the row keeps the state it
 * had and speaks the typed error. A batch that cannot reach the server at all
 * stops the pass, leaving the queue for the next reconnect. Single-flighted.
 */
export async function replayQueue(): Promise<void> {
  if (queue.replaying || queue.entries.length === 0 || isOffline()) return;
  queue.replaying = true;
  try {
    for (const batch of batches(queue.entries)) {
      let results: ReplayResult[];
      try {
        results = await sendBatch(batch);
      } catch {
        return; // still unreachable: keep the queue, try again on the next event
      }
      const folded = reconcileResults(queue.entries, results);
      queue.entries = folded.queue;
      const notices = new Map(queue.state.notices);
      for (const r of folded.refused) notices.set(r.workItemId, errorMessage(r.error));
      void persistQueue([], folded.settled);
      publish({ notices, full: false });
      // The server's rows for what just landed, or for what someone else had
      // already done: the pending overlay is gone, so the surfaces must refresh
      // rather than fall back to the state the acts were taken against
      // (docs/SYNC.md §7.3).
      if (folded.accepted.length > 0 || folded.superseded.length > 0) onReplaySettled?.();
    }
  } finally {
    queue.replaying = false;
  }
}

/** Clear the notice a refused replay left on one item (the row speaks it once). */
export function clearNotice(workItemId: string): void {
  if (!queue.state.notices.has(workItemId)) return;
  const notices = new Map(queue.state.notices);
  notices.delete(workItemId);
  publish({ notices });
}

/**
 * One batch to the server: `replayCommands` (src/app/actions.ts -> the gated
 * `replayCommands` of src/server/replay.ts, batch <= 100, every command decided
 * individually against the state at arrival, `occurredAt` clamped into the
 * trust window). A whole-batch refusal (the two bounds of §7.4) answers with a
 * typed error and nothing settled, so the queue keeps everything for the next
 * attempt.
 */
async function sendBatch(batch: StoredCommand[]): Promise<ReplayResult[]> {
  const { replayCommands } = await import('@/app/actions');
  const answer = await replayCommands({
    commands: batch.map((e) => ({
      key: e.key,
      workItemId: e.workItemId,
      ...(e.command ? { command: e.command } : {}),
      // The stimulus with its payload, the surface and what the tap expected
      // (so the replay can tell "still applies" from "superseded",
      // docs/RESOLUTION.md §9). The swept expectations are the overlay's
      // alone; the replay recomputes the sweep at arrival.
      ...(e.gesture ? { gesture: wireGesture(e.gesture) } : {}),
      occurredAt: e.occurredAt,
      basedOn: e.basedOn,
    })),
  });
  return 'results' in answer ? answer.results : [];
}

/** A stored gesture as the replay endpoint takes it: everything but the
 *  overlay's swept expectations. Pure. */
export function wireGesture(gesture: StoredGesture): QueuedGesture {
  const wire: QueuedGesture & { swept?: unknown } = { ...gesture };
  delete wire.swept;
  return wire;
}
